import { Component, ElementRef, OnInit, Input } from '@angular/core';
import { CfCoreComponent } from '../core/core.component';
import { CfCoreGalleryImage } from '../../models/gallery/cf-core-gallery-image.model';
import { CfCoreGalleryOptions } from '../../models/gallery/cf-core-gallery-options.model';
import { GalleryModel } from '../../models/gallery/gallery.model';
import { GalleryStylingModel } from '../../models/gallery/gallery-styling.model';
import { GalleryTemplates } from '../../templates/gallery.template';
import { TemplateService } from '../../services/template-service/template.service';
/**
 * <p>CF Gallery component is created using functionality from
 * <a href="https://github.com/lukasz-galka/ngx-gallery" target="_blank">Ngx-gallery</a></p>
 * <pre>
 * <code>
 * import {{'{'}} GalleryModel {{'}'}} from 'cedrus-fusion'
 * import {{'{'}} GalleryStylingModel {{'}'}} from 'cedrus-fusion'
 * <code><</code>cf-gallery<code>></code><code><</code><code>/</code>cf-gallery<code>></code>
 * </code>
 * </pre>
 */
export class CfGalleryComponent extends CfCoreComponent implements OnInit {
/**
 * <pre>{{'{'}}
 *  display: boolean,                      // True or False. Default: true
 *  options: {{'{'}}                        // Object with specific gallery library options
 *    width: string,                       // Default: 100% - gallery width
 *    height: string,                      // Default: 400px - gallery height
 *    fullWidth: boolean,                  // Default: false - sets the same width as browser
 *    image: boolean,                      // Default: true - enables or disables image
 *    imagePercent: number,                // Default: 75 - percentage height
 *    imageArrows: boolean,                // Default: true - enables or disables arrows
 *    imageArrowsAutoHide: boolean,        // Default: false - enables or disables arrows auto hide
 *    imageSwipe: boolean,                 // Default: false - enables or disables swipe
 *    imageAnimation: string,              // Default: fade - animation type. Types are: fade, slide, rotate, zoom.
 *    imageSize: string,                   // Default: cover - image size. Sizes are: cover and contain.
 *    thumbnails: boolean,                 // Default: true - enables or disables thumbnails
 *    thumbnailsColumns: number,           // Default: 4 - columns count
 *    thumbnailsRows: number,              // Default: 1 - rows count
 *    thumbnailsPercent: number,           // Default: 25 - percentage height
 *    thumbnailsMargin: number,            // Default: 10 - margin between thumbnails and image
 *    thumbnailsArrows: boolean,           // Default: true - enables or disables arrows
 *    thumbnailsArrowsAutoHide: boolean,   // Default: false - enables or disables arrows auto hide
 *    thumbnailsSwipe: boolean,            // Default: false - enables or disables swipe
 *    thumbnailMargin: number,             // Default: 10 - margin between images in thumbnails
 *    preview: boolean,                    // Default: true - enables or disables preview
 *    previewDescription: boolean,         // Default: true - enables or disables description for images
 *    previewSwipe: boolean,               // Default: false - enables or disables swipe
 *    previewCloseOnClick: boolean,        // Default: false - enables or disables closing preview by click
 *    previewKeyboardNavigation: boolean,  // Default: false - enables or disables navigation by keyboard
 *  {{'}'}},
 *  images: array                          // Array with image objects like in example below
 * </pre>
 * {{'}'}}
 * Example:
 * <pre>{{'{'}}
 *  options: {{'{'}}
 *    width: '100%',
 *    imagePercent: 75,
 *    imageArrowsAutoHide: true,
 *    thumbnailsPercent: 25,
 *    thumbnailsColumns: 3,
 *    thumbnailMargin: 8,
 *    thumbnailsMargin: 8,
 *    thumbnailsArrowsAutoHide: true,
 *    previewFullscreen: true
 *  {{'}'}},
 *  images: [
 *    {{'{'}}
 *      small: 'https://i1.wallpaperscraft.com/image/nature_waterfall_summer_lake_trees_90400_300x168.jpg',
 *      medium: 'https://i1.wallpaperscraft.com/image/nature_waterfall_summer_lake_trees_90400_602x339.jpg',
 *      big: 'https://wallpaperscraft.com/image/nature_waterfall_summer_lake_trees_90400_1920x1080.jpg',
 *      description: 'Nature waterfall summer lake_trees'
 *    {{'}'}},
 *    ...
 *  ]
 * {{'}'}}</pre>
 */
properties: GalleryModel;
/**
 * <p> dynamicClass: function() -> string,  // Function that returns name of the class </p>
 * <p> class: string                        // Name of the css class selector </p>
 * <pre>
 * {{'{'}}
 *  // Container surrounding the Gallery
 *  container:{{'{'}}
 *    dynamicClass,
 *    class
 *  {{'}'}}
 * {{'}'}}
 * </pre>
 */
styling: GalleryStylingModel;
/**
 * <p>Array with images of type CfCoreGalleryImage[]. Each image is an object with such properties:<p>
 * small: string,       // Url used in thumbnails
 * medium: string,      // Url used in image
 * big: string,         // Url used in preview
 * description: string  // Description text used in preview
 * @param {?} value
 * @return {?}
 */
set images(value: CfCoreGalleryImage[]) { this.properties.images = value; }
/**
 * <p>Gallery settings of type CfCoreGalleryOptions described in <b>properties</b><p>
 * @param {?} value
 * @return {?}
 */
set options(value: CfCoreGalleryOptions) { this.properties.options = value; }
/**
 * @hidden
 * @param {?} elementRef
 * @param {?} templateService
 */
public constructor(public elementRef: ElementRef, /**@hidden */ templateService: TemplateService) {
    super(elementRef, templateService);
    this.properties = new GalleryModel();
    this.styling = new GalleryStylingModel();
  }
/**
 * @hidden 
 * It will be generated <b>cfGallery</b> object and rendered inside component template.
 * @return {?}
 */
ngOnInit() {
    this.getMyTemplate('gallery', GalleryTemplates).then(() => {
      this.mergeProperties(this.activeTemplate.property, this.properties, 'GALLERY').then( sampler => {
        let /** @type {?} */ active = new GalleryStylingModel(this.activeTemplate.style);
        this.styling = Object.assign({}, active, this.styling);
        // console.log('GALLERY', this);
      });
    });
  }
static decorators: DecoratorInvocation[] = [
{ type: Component, args: [{
  selector: 'cf-gallery',
  template: "<div *ngIf=\"_inited && properties?.display\" class=\"cf-gallery {{styling?.container?.class}}\" [ngClass]=\"getDynamicClasses(styling?.container?.dynamicClass)\"> <cf-core-gallery [options]=\"properties?.options\" [images]=\"properties?.images\"></cf-core-gallery> </div>",
  styles: [".cf-gallery { display: flex; } .cf-gallery.cf-black-background-border { background: black; padding: 1px; } "]
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: ElementRef, },
{type: TemplateService, },
];
static propDecorators: {[key: string]: DecoratorInvocation[]} = {
'properties': [{ type: Input },],
'styling': [{ type: Input },],
'images': [{ type: Input, args: ['images', ] },],
'options': [{ type: Input, args: ['options', ] },],
};
}

function CfGalleryComponent_tsickle_Closure_declarations() {
/** @type {?} */
CfGalleryComponent.decorators;
/**
 * @nocollapse
 * @type {?}
 */
CfGalleryComponent.ctorParameters;
/** @type {?} */
CfGalleryComponent.propDecorators;
/**
 * <pre>{{'{'}}
 *  display: boolean,                      // True or False. Default: true
 *  options: {{'{'}}                        // Object with specific gallery library options
 *    width: string,                       // Default: 100% - gallery width
 *    height: string,                      // Default: 400px - gallery height
 *    fullWidth: boolean,                  // Default: false - sets the same width as browser
 *    image: boolean,                      // Default: true - enables or disables image
 *    imagePercent: number,                // Default: 75 - percentage height
 *    imageArrows: boolean,                // Default: true - enables or disables arrows
 *    imageArrowsAutoHide: boolean,        // Default: false - enables or disables arrows auto hide
 *    imageSwipe: boolean,                 // Default: false - enables or disables swipe
 *    imageAnimation: string,              // Default: fade - animation type. Types are: fade, slide, rotate, zoom.
 *    imageSize: string,                   // Default: cover - image size. Sizes are: cover and contain.
 *    thumbnails: boolean,                 // Default: true - enables or disables thumbnails
 *    thumbnailsColumns: number,           // Default: 4 - columns count
 *    thumbnailsRows: number,              // Default: 1 - rows count
 *    thumbnailsPercent: number,           // Default: 25 - percentage height
 *    thumbnailsMargin: number,            // Default: 10 - margin between thumbnails and image
 *    thumbnailsArrows: boolean,           // Default: true - enables or disables arrows
 *    thumbnailsArrowsAutoHide: boolean,   // Default: false - enables or disables arrows auto hide
 *    thumbnailsSwipe: boolean,            // Default: false - enables or disables swipe
 *    thumbnailMargin: number,             // Default: 10 - margin between images in thumbnails
 *    preview: boolean,                    // Default: true - enables or disables preview
 *    previewDescription: boolean,         // Default: true - enables or disables description for images
 *    previewSwipe: boolean,               // Default: false - enables or disables swipe
 *    previewCloseOnClick: boolean,        // Default: false - enables or disables closing preview by click
 *    previewKeyboardNavigation: boolean,  // Default: false - enables or disables navigation by keyboard
 *  {{'}'}},
 *  images: array                          // Array with image objects like in example below
 * </pre>
 * {{'}'}}
 * Example:
 * <pre>{{'{'}}
 *  options: {{'{'}}
 *    width: '100%',
 *    imagePercent: 75,
 *    imageArrowsAutoHide: true,
 *    thumbnailsPercent: 25,
 *    thumbnailsColumns: 3,
 *    thumbnailMargin: 8,
 *    thumbnailsMargin: 8,
 *    thumbnailsArrowsAutoHide: true,
 *    previewFullscreen: true
 *  {{'}'}},
 *  images: [
 *    {{'{'}}
 *      small: 'https://i1.wallpaperscraft.com/image/nature_waterfall_summer_lake_trees_90400_300x168.jpg',
 *      medium: 'https://i1.wallpaperscraft.com/image/nature_waterfall_summer_lake_trees_90400_602x339.jpg',
 *      big: 'https://wallpaperscraft.com/image/nature_waterfall_summer_lake_trees_90400_1920x1080.jpg',
 *      description: 'Nature waterfall summer lake_trees'
 *    {{'}'}},
 *    ...
 *  ]
 * {{'}'}}</pre>
 * @type {?}
 */
CfGalleryComponent.prototype.properties;
/**
 * <p> dynamicClass: function() -> string,  // Function that returns name of the class </p>
 * <p> class: string                        // Name of the css class selector </p>
 * <pre>
 * {{'{'}}
 *  // Container surrounding the Gallery
 *  container:{{'{'}}
 *    dynamicClass,
 *    class
 *  {{'}'}}
 * {{'}'}}
 * </pre>
 * @type {?}
 */
CfGalleryComponent.prototype.styling;
/** @type {?} */
CfGalleryComponent.prototype.elementRef;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
